// services/analytics.ts
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { AppType } from '@/types';
import { getProduct } from '@/product';
import { getChosenThemePreference, getResolvedTheme, type ThemePreference } from '@/theme/theme';
import type { ProductEventDef } from './productEvents';

/** Event properties and person traits, as both PostHog and Ripples take them. */
export type AnalyticsProps = Record<string, unknown>;

/** `window.ripples`: v.js itself, or the queue stub that stands in until it loads. */
type RipplesFn = {
    (...args: unknown[]): void;
    q?: unknown[][];
    identify: (props: AnalyticsProps) => void;
    track: (name: string, props?: AnalyticsProps) => void;
    pageview: () => void;
};

declare global {
    interface Window {
        ripples?: RipplesFn;
    }
}

/** The identity signature PostHog last received from this device. */
const POSTHOG_IDENTITY_KEY = 'analytics_posthog_identity';

// Storage can throw (private mode, blocked site data); then PostHog just gets
// every identify, as before.
function readStored(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStored(key: string, value: string | null): void {
    try {
        if (value === null) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
    } catch {
        // Nothing to keep; the next launch re-sends.
    }
}

class AnalyticsService {
    private initialized = false;
    private currentAppType: AppType | null = null;
    private currentUserId: string | null = null;
    private colorSchemeListenerAttached = false;

    /** The traits last sent, so a repeat identify with nothing new is a no-op. */
    private lastIdentity: string | null = null;
    private lastTraits: AnalyticsProps | null = null;

    /**
     * An identify that arrived before initialize().
     *
     * Ripples asks for identify on every authenticated load, and on a cold
     * start the user can be restored from cache before PlatformContext has
     * finished detecting the app type. Without this the very first identify —
     * the one that dates the signup — was dropped.
     */
    private pendingIdentity: { userId: string | number; props?: AnalyticsProps } | null = null;

    /**
     * Detect the system color scheme (follows OS dark/light setting, incl. in the webview)
     */
    private getColorScheme(): 'dark' | 'light' {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return 'light';
        }
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    /**
     * Watch for OS theme changes and update the user property on the fly
     */
    private watchColorScheme() {
        if (this.colorSchemeListenerAttached || typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        this.colorSchemeListenerAttached = true;

        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
            const color_scheme = e.matches ? 'dark' : 'light';

            if (this.initialized) {
                posthog.setPersonProperties({ color_scheme });
            }

            // Ripples updates user props via identify, and an identify replaces
            // what it carries — so re-send the whole trait set with the new
            // scheme rather than an id-and-colour pair that drops the rest.
            if (this.currentUserId && this.lastTraits) {
                this.identify(this.currentUserId, { ...this.lastTraits, color_scheme });
            }
        });
    }

    /**
     * Initialize PostHog with the correct API key based on appType
     */
    initialize(appType: AppType) {
        // Build mode alone is insufficient: a production bundle may be served
        // on stage, and native/live-reload builds may point at a dev host.
        if (import.meta.env.VITE_ENVIRONMENT !== 'production' ||
            typeof window === 'undefined' ||
            window.location.hostname !== new URL(getProduct(appType).appUrl).hostname) {
            return;
        }

        if (this.initialized && this.currentAppType === appType) {
            return; // Already initialized with the same appType
        }

        const { posthogKey, ripplesToken } = getProduct(appType);

        if (!posthogKey) {
            console.warn(`PostHog key not found for appType: ${appType}`);
            return;
        }

        posthog.init(posthogKey, {
            api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
            person_profiles: 'always',
            capture_pageview: false,
            capture_pageleave: false,
            // Every tap became a $autocapture event: 232k of Hallouminati's
            // 941k a month (2026-10), with the free tier at 1M, and no insight
            // read them. What we measure is logged by name.
            autocapture: false,
            capture_performance: { web_vitals: false },
            defaults: '2025-11-30',
        });

        // Initialize Ripples.sh analytics
        if (ripplesToken && typeof document !== 'undefined') {
            // The stub has to exist BEFORE the tag, because the tag is
            // deferred: on a cold start the user is often known — and
            // identify called — a second or two before cdn.ripples.sh has
            // finished loading. Previously every one of those calls hit
            // `if (window.ripples)`, found nothing and vanished, which on a
            // fresh install is the identify that dates the signup.
            //
            // v.js replays `window.ripples.q` on load, applying each entry as
            // an argument list, so the queue only has to push arguments in the
            // shape the real function takes.
            this.installRipplesQueue();
            void this.loadRipplesTracker(ripplesToken);
        }

        this.initialized = true;
        this.currentAppType = appType;
        this.watchColorScheme();

        console.log(`Analytics initialized for ${appType}`);

        // Someone identified while we were still detecting the platform.
        const pending = this.pendingIdentity;
        if (pending) {
            this.pendingIdentity = null;
            this.identify(pending.userId, pending.props);
        }
    }

    /**
     * Load Ripples with the identity of the native binary, not its WebView.
     *
     * Hallouminati and GreekBrain share this JavaScript bundle, so hard-coding
     * either name or version would be wrong for the other target. App.getInfo()
     * reads the active target's own label and bundled version. Ripples maps
     * those values to browser/browser_version, matching its native iOS SDK.
     */
    private async loadRipplesTracker(token: string): Promise<void> {
        try {
            const script = document.createElement('script');
            script.defer = true;
            script.src = 'https://cdn.ripples.sh/v.js';
            script.dataset.token = token;

            if (Capacitor.isNativePlatform()) {
                script.dataset.platform = Capacitor.getPlatform();
                script.dataset.sdkName = 'capacitor';

                try {
                    const info = await App.getInfo();
                    script.dataset.appName = info.name;
                    script.dataset.appVersion = info.version;
                } catch (error) {
                    // Bundle metadata is an enhancement, not a reason to lose
                    // the visit. Keep the UA fallback, but make the failure
                    // observable because it degrades every native session.
                    Sentry.captureException(error);
                    console.warn('Failed to read native app identity for Ripples:', error);
                }
            }

            document.head.appendChild(script);
        } catch (error) {
            Sentry.captureException(error);
            console.warn('Failed to load Ripples tracker:', error);
        }
    }

    /**
     * The standard async-snippet stub: queue calls until v.js replaces it.
     *
     * Mirrors the three methods v.js defines (`identify`, `track`, `pageview`)
     * plus the bare call form, so nothing between injection and load is lost.
     */
    private installRipplesQueue() {
        if (typeof window === 'undefined' || window.ripples) {
            return;
        }

        const stub = function (...args: unknown[]) {
            (stub.q = stub.q || []).push(args);
        } as RipplesFn;
        stub.q = [];
        stub.identify = (props: AnalyticsProps) => stub('identify', props);
        stub.track = (name: string, props?: AnalyticsProps) => stub('track', name, props);
        stub.pageview = () => stub('pageview');

        window.ripples = stub;
    }

    /**
     * Log an event with optional properties
     */
    logEvent(eventName: string, eventProps?: AnalyticsProps) {
        if (!this.initialized) {
            return;
        }

        if (typeof posthog !== 'undefined') {
            posthog.capture(eventName, eventProps);
        }
    }

    /**
     * Identify a user.
     *
     * Ripples asks for this on **every authenticated page load, not just at
     * login** — repeated calls are safe and only the first one Ripples ever
     * sees synthesises a signup, so there is no metric to inflate. In an SPA
     * "page load" means app launch, so `useAnalyticsIdentity` fires it from
     * the store's user; the profile query fires it again when it brings back
     * fresher traits. Both land here, and the signature check below makes the
     * overlap free.
     *
     * `signed_up_at` matters more than it looks: if the first identify Ripples
     * sees for a user carries no signup date, the account is dated "now" and
     * the signups chart and retention cohorts are wrong from then on.
     */
    identify(userId: string | number, userProps?: AnalyticsProps) {
        const email = userProps?.email;
        const name = userProps?.name;
        Sentry.setUser({
            id: String(userId),
            email: typeof email === 'string' ? email : undefined,
            username: typeof name === 'string' ? name : undefined,
        });

        // Anonymous users have a backend user row (and created_at) but haven't signed up.
        // Identifying them in product analytics would mark them as signed-up users.
        if (userProps?.is_anonymous) {
            return;
        }

        // Called before PlatformContext finished detecting the app type. Hold
        // it rather than dropping it — initialize() replays the last one.
        if (!this.initialized) {
            this.pendingIdentity = { userId, props: userProps };
            return;
        }

        const traits = this.buildTraits(userProps);
        const signature = `${userId}:${JSON.stringify(traits)}`;

        // Nothing has changed since the last call this session. The load-time
        // identify already happened; re-sending it would only add events.
        if (signature === this.lastIdentity) {
            return;
        }

        this.currentUserId = String(userId);
        this.lastIdentity = signature;
        this.lastTraits = traits;

        // PostHog keeps person properties, so re-sending the traits it already
        // has from this device only adds a $set event, one per launch: 55k a
        // month (2026-10). Send them when the person or a trait changed.
        if (this.initialized &&
            (posthog.get_distinct_id() !== String(userId) || readStored(POSTHOG_IDENTITY_KEY) !== signature)) {
            posthog.identify(String(userId), traits);
            writeStored(POSTHOG_IDENTITY_KEY, signature);
        }

        // Always defined once initialize() has run: the queue stub stands in
        // until v.js loads and then replays what it collected.
        if (this.initialized && typeof window !== 'undefined' && window.ripples) {
            window.ripples.identify({ id: userId, ...traits });
        }
    }

    /**
     * The trait set both tools receive.
     *
     * Ripples takes string | number values, so undefined keys are dropped
     * rather than sent — a payload that predates a field (a cached profile,
     * say) must not blank that field on the person record.
     */
    private buildTraits(userProps?: AnalyticsProps): AnalyticsProps {
        const traits: AnalyticsProps = {
            ...userProps,
            color_scheme: this.getColorScheme(),
            signed_up_at: userProps?.created_at,
            ...this.themeTraits(),
        };

        for (const key of Object.keys(traits)) {
            if (traits[key] === undefined || traits[key] === null) {
                delete traits[key];
            }
        }

        return traits;
    }

    /**
     * The app theme next to the phone's `color_scheme`, Hallouminati only
     * (GreekBrain is always light). `theme_preference` is what the user picked
     * in Profile › Appearance, or 'default' if they never did; `theme` is what
     * is on screen (dark-mode-plan.md, Phase 5).
     */
    private themeTraits(): Record<string, string> {
        if (this.currentAppType !== 'hallouminati') return {};
        return {
            theme_preference: getChosenThemePreference() ?? 'default',
            theme: getResolvedTheme(),
        };
    }

    /**
     * Profile › Appearance changed. Logs the switch and re-sends the last trait
     * set, which re-reads the theme traits, so the person record follows.
     */
    trackThemeChange(from: ThemePreference | 'default', to: ThemePreference) {
        this.logEvent('Theme Changed', { from, to, theme: getResolvedTheme() });
        if (this.currentUserId && this.lastTraits) {
            this.identify(this.currentUserId, this.lastTraits);
        }
    }

    /**
     * Track a product usage event to both Ripples and PostHog
     */
    trackProduct(event: ProductEventDef, extraProps?: AnalyticsProps) {
        if (!this.initialized) return;

        const properties: AnalyticsProps = {
            area: event.area,
            ...(event.activated && { activated: true }),
            ...extraProps,
        };

        // Ripples
        if (typeof window !== 'undefined' && window.ripples) {
            window.ripples.track(event.name, properties);
        }

        // PostHog
        if (this.initialized && typeof posthog !== 'undefined') {
            posthog.capture(event.name, properties);
        }
    }

    /**
     * Reset user identity (e.g., on logout)
     */
    reset() {
        Sentry.setUser(null);
        this.currentUserId = null;
        this.lastIdentity = null;
        this.lastTraits = null;
        this.pendingIdentity = null;
        writeStored(POSTHOG_IDENTITY_KEY, null);
        if (this.initialized) {
            posthog.reset();
        }
    }
}

export const analyticsService = new AnalyticsService();

// Export as appAnalytics for compatibility with legacy usage
export const appAnalytics = analyticsService;

// Also make it available on window for console debugging
if (typeof window !== 'undefined') {
    window.appAnalytics = analyticsService;
}
